import { Action, DEFAULT_LIMIT, DEFAULT_PAGE } from "@pilatestime/shared-types";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { PaginationMeta } from "@/components/data-table";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { InvoiceList } from "./-components/InvoiceList";

// Search params schema
const billingSearchSchema = z.object({
	page: z.number().min(1).catch(DEFAULT_PAGE),
	limit: z.number().min(1).max(100).catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
});

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/billing/")({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		return { userId, token, orgId: params.orgId };
	},
	validateSearch: billingSearchSchema,
	component: BillingIndexPage,
});

function BillingIndexPage() {
	const { t } = useTranslation();
	const { userId, token, orgId } = Route.useLoaderData();
	const search = Route.useSearch();
	const navigate = useNavigate();

	// Access control
	const can = useCan();
	const canManageBilling = can(Action.MANAGE_PAYMENTS);

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Navigate to invoice details page
	const handleViewInvoice = useCallback(
		(invoiceId: string) => {
			navigate({
				to: "/app/org/$orgId/billing/$invoiceId/overview",
				params: { orgId, invoiceId },
			});
		},
		[navigate, orgId],
	);

	// Debounce search term
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1);
		}, 300);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Pagination handlers
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (limit: number) => {
		setPageSize(limit);
		setCurrentPage(1);
	};

	const handlePaginationMetaChange = useCallback((meta: PaginationMeta) => {
		setPaginationMeta(meta);
	}, []);

	return (
		<div className="h-full flex flex-col overflow-hidden space-y-4">
			<PageHeader
				title={t("billing.invoices.title")}
				description={t("billing.invoices.description")}
			/>

			<Card className="flex-1 flex flex-col overflow-hidden">
				<CardHeader className="flex-shrink-0 pb-4">
					<div className="flex flex-col sm:flex-row gap-4">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50" />
							<Input
								placeholder={t("common.search")}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10"
								data-testid="billing-search-input"
							/>
						</div>
						{canManageBilling && (
							<Button asChild>
								<Link to="/app/org/$orgId/billing/new" params={{ orgId }}>
									<Plus className="h-4 w-4 mr-2" />
									{t("billing.invoices.actions.create")}
								</Link>
							</Button>
						)}
					</div>
				</CardHeader>
				<CardContent className="flex-1 overflow-hidden flex flex-col space-y-4">
					<div className="flex-shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>
					<div className="flex-1 overflow-hidden">
						<InvoiceList
							organizationId={orgId}
							pagination={{ page: currentPage, limit: pageSize }}
							searchTerm={debouncedSearchTerm}
							userId={userId}
							token={token}
							canManageBilling={canManageBilling}
							onPaginationMetaChange={handlePaginationMetaChange}
							onViewInvoice={handleViewInvoice}
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
